import * as CookieConsent from "vanilla-cookieconsent";
import { appConfig } from "@lib/config";
import { Scroll } from "@lib/scroll";

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** Service-IDs für kontextbezogene Freigabe (data-consent-accept / data-consent-placeholder). */
export const CONTEXTUAL_CONSENT_SERVICES = {
  youtube: { serviceName: "YouTube", category: "marketing" as const },
  vimeo: { serviceName: "Vimeo", category: "marketing" as const },
  pipedrive: { serviceName: "Pipedrive Webform", category: "functionality" as const },
} as const;

export type ContextualConsentServiceId = keyof typeof CONTEXTUAL_CONSENT_SERVICES;

/** Detail für consent:unlock Events. */
export interface ConsentUnlockDetail {
  serviceId: ContextualConsentServiceId;
  serviceName: string;
  category: string;
}

type ConsentState = "granted" | "denied";

function getConsentState(): {
  analytics_storage: ConsentState;
  ad_storage: ConsentState;
  ad_user_data: ConsentState;
  ad_personalization: ConsentState;
  functionality_storage: ConsentState;
  personalization_storage: ConsentState;
} {
  const marketing = CookieConsent.acceptedCategory("marketing");
  const functionality = CookieConsent.acceptedCategory("functionality");

  // GA4 läuft nur über GTM: analytics_storage nur "granted", wenn beide Dienste akzeptiert.
  // Bei Ablehnung von GTM wird GTM nicht geladen (GA4 kann nicht laufen); der State bleibt konsistent.
  const gtmAccepted = CookieConsent.acceptedService("Google Tag Manager", "analytics");
  const gaAccepted = CookieConsent.acceptedService("Google Analytics", "analytics");
  const analytics_storage: ConsentState =
    gtmAccepted && gaAccepted ? "granted" : "denied";

  return {
    analytics_storage,
    ad_storage: marketing ? "granted" : "denied",
    ad_user_data: marketing ? "granted" : "denied",
    ad_personalization: marketing ? "granted" : "denied",
    functionality_storage: functionality ? "granted" : "denied",
    personalization_storage: functionality ? "granted" : "denied",
  };
}

/**
 * Pusht Consent State an die dataLayer für Google Consent Mode v2.
 * Muss VOR dem Laden von GTM aufgerufen werden.
 */
function pushConsentDefault(consent: ReturnType<typeof getConsentState>) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "consent_default",
    consent_default: consent,
  });
}

/**
 * Pusht Consent-Update an die dataLayer (bei Änderung der Präferenzen).
 */
function pushConsentUpdate(consent: ReturnType<typeof getConsentState>) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "consent_update",
    consent_update: consent,
  });
}

let gtmLoaded = false;

/**
 * Load Google Tag Manager when consent is given.
 * Consent Mode v2: consent_default wird VOR GTM gepusht, damit GA4 in GTM den Status kennt.
 * Guard verhindert doppeltes Laden (onAccept + onConsent rufen beide loadServicesIfAccepted/loadGTM aus).
 */
function loadGTM(containerId: string) {
  if (!containerId || gtmLoaded) return;
  gtmLoaded = true;

  window.dataLayer = window.dataLayer || [];

  pushConsentDefault(getConsentState());

  window.dataLayer.push({
    "gtm.start": new Date().getTime(),
    event: "gtm.js",
  });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
  document.head.appendChild(script);
}

/** Event-Name für kontextbezogene Freigabe (detail: ConsentUnlockDetail). */
export const CONSENT_UNLOCK_EVENT = "consent:unlock";

/** Erlaubte Kategorien (Kleinbuchstaben wie in CookieConsent). */
const CONSENT_CATEGORIES = ["necessary", "functionality", "analytics", "marketing"] as const;

/** data-service Werte → exakter Service-Name in der CMP-Config. */
const SERVICE_NAME_MAP: Record<string, string> = {
  youtube: "YouTube",
  vimeo: "Vimeo",
  "pipedrive webform": "Pipedrive Webform",
};

function normalizeServiceName(attr: string): string {
  const key = attr.trim().toLowerCase();
  return SERVICE_NAME_MAP[key] ?? attr.trim();
}

function normalizeCategory(attr: string): string {
  const cat = attr.trim().toLowerCase();
  return CONSENT_CATEGORIES.includes(cat as (typeof CONSENT_CATEGORIES)[number]) ? cat : "marketing";
}

/**
 * Sucht die Embed-Komponente (data-iframe-url/data-service oder data-module="pipedrive-webforms"/data-service) die den gegebenen Button enthält.
 */
function getEmbedComponent(button: Element): HTMLElement | null {
  return (
    button.closest("[data-iframe-url][data-service]") ??
    button.closest("[data-module=\"pipedrive-webforms\"][data-service]")
  ) as HTMLElement | null;
}

const PIPEDRIVE_LOADER_URL = "https://webforms.pipedrive.com/f/loader";

/**
 * Setzt data-consent-given="true" und lädt Pipedrive Webforms (Script-Injection).
 * Das Script wird als Kind von [data-pd-webforms] eingefügt (Child des Moduls data-module="pipedrive-webforms").
 * Das Pipedrive-Loader-Script sucht nach [data-pd-webforms] und rendert die Formulare.
 */
function unlockPipedriveComponent(component: HTMLElement): void {
  component.setAttribute("data-consent-given", "true");
  if (document.querySelector(`script[src="${PIPEDRIVE_LOADER_URL}"]`)) return;
  const container = component.querySelector<HTMLElement>("[data-pd-webforms]");
  if (!container) return;
  const script = document.createElement("script");
  script.src = PIPEDRIVE_LOADER_URL;
  container.appendChild(script);
}

/**
 * Setzt data-consent-given="true" und fügt den iframe mit data-iframe-url als Kind ein.
 * Bei Pipedrive-Webforms wird stattdessen das Loader-Script injiziert.
 * CSS kann [data-consent-given="true"] .video_consent-view { display: none } nutzen.
 * Nach dem Laden des iframes wird das Scroll-System (Lenis) aktualisiert.
 */
function unlockEmbedComponent(component: HTMLElement): void {
  if (component.getAttribute("data-module") === "pipedrive-webforms") {
    unlockPipedriveComponent(component);
    return;
  }
  const url = component.getAttribute("data-iframe-url");
  if (!url) return;

  component.setAttribute("data-consent-given", "true");

  const iframe = document.createElement("iframe");
  iframe.src = url;
  iframe.setAttribute("title", component.getAttribute("data-service") || "Embed");
  iframe.loading = "lazy";
  iframe.addEventListener("load", () => {
    Scroll.resize();
  });
  component.appendChild(iframe);
}

const EMBED_CONSENT_PENDING_SELECTOR =
  "[data-iframe-url][data-consent-given=\"false\"], [data-module=\"pipedrive-webforms\"][data-consent-given=\"false\"]";

/**
 * Prüft alle Embed- und Pipedrive-Komponenten mit data-consent-given="false" und schaltet frei, wo Consent bereits erteilt.
 */
function unlockEmbedComponentsIfAccepted(): void {
  if (!appConfig.enableCMP) {
    document.querySelectorAll<HTMLElement>(EMBED_CONSENT_PENDING_SELECTOR).forEach((el) => unlockEmbedComponent(el));
    return;
  }
  document.querySelectorAll<HTMLElement>(EMBED_CONSENT_PENDING_SELECTOR).forEach((component) => {
    const serviceAttr = component.getAttribute("data-service");
    const categoryAttr = component.getAttribute("data-category");
    if (!serviceAttr) return;
    const serviceName = normalizeServiceName(serviceAttr);
    const category = normalizeCategory(categoryAttr ?? "");
    if (CookieConsent.acceptedService(serviceName, category)) {
      unlockEmbedComponent(component);
    }
  });
}

/**
 * Prüft, ob der angegebene kontextbezogene Service bereits akzeptiert wurde.
 */
export function isContextualServiceAccepted(
  serviceId: ContextualConsentServiceId
): boolean {
  if (!appConfig.enableCMP) return true;
  const config = CONTEXTUAL_CONSENT_SERVICES[serviceId];
  return CookieConsent.acceptedService(config.serviceName, config.category);
}

/**
 * Prüft, ob ein Service in einer Kategorie akzeptiert wurde (für data-service/data-category Komponenten).
 */
export function isServiceAcceptedInCategory(
  serviceName: string,
  category: string
): boolean {
  if (!appConfig.enableCMP) return true;
  const normalized = normalizeServiceName(serviceName);
  const cat = normalizeCategory(category);
  return CookieConsent.acceptedService(normalized, cat);
}

/**
 * Akzeptiert einen kontextbezogenen Service programmatisch und löst consent:unlock aus.
 * Nützlich, wenn du nicht über einen Button steuerst (z. B. bei eigener UI).
 */
export function acceptContextualService(
  serviceId: ContextualConsentServiceId
): void {
  const config = CONTEXTUAL_CONSENT_SERVICES[serviceId];
  if (
    appConfig.enableCMP &&
    !CookieConsent.acceptedService(config.serviceName, config.category)
  ) {
    CookieConsent.acceptService(config.serviceName, config.category);
  }
  document.dispatchEvent(
    new CustomEvent<ConsentUnlockDetail>(CONSENT_UNLOCK_EVENT, {
      detail: {
        serviceId,
        serviceName: config.serviceName,
        category: config.category,
      },
    })
  );
}

/**
 * Bindet Klicks auf [data-consent-accept] und Embed-Komponenten mit data-iframe-url.
 * - Wenn der Button in einer Komponente mit data-iframe-url + data-service + data-category steht:
 *   Service in dieser Kategorie akzeptieren, data-consent-given="true" setzen, iframe aus data-iframe-url einfügen.
 * - Sonst: bisheriges Verhalten (Service-ID aus data-consent-accept, consent:unlock Event).
 */
/**
 * Öffnet das Consent-Einstellungs-Modal (Präferenzen).
 * Wird z. B. vom Footer-Button [data-trigger="show-consent-overlay"] aufgerufen.
 */
export function showConsentPreferences(): void {
  if (appConfig.enableCMP) {
    CookieConsent.showPreferences();
  }
}

function initContextualConsent(): void {
  document.body.addEventListener("click", (evt: MouseEvent) => {
    const trigger = (evt.target as Element)?.closest?.(
      "[data-trigger=\"show-consent-overlay\"]"
    ) as HTMLElement | null;
    if (trigger) {
      evt.preventDefault();
      showConsentPreferences();
      return;
    }

    const button = (evt.target as Element)?.closest?.(
      "[data-consent-accept]"
    ) as HTMLElement | null;
    if (!button) return;

    const component = getEmbedComponent(button);
    if (component) {
      const serviceAttr = component.getAttribute("data-service");
      const categoryAttr = component.getAttribute("data-category");
      if (serviceAttr) {
        evt.preventDefault();
        // Zuerst Komponente freischalten, damit CMP-Callbacks (onConsent/cc:onChange)
        // bei unlockEmbedComponentsIfAccepted() diese Komponente nicht erneut laden.
        unlockEmbedComponent(component);
        const serviceName = normalizeServiceName(serviceAttr);
        const category = normalizeCategory(categoryAttr ?? "");
        if (
          appConfig.enableCMP &&
          !CookieConsent.acceptedService(serviceName, category)
        ) {
          CookieConsent.acceptService(serviceName, category);
        }
      }
      return;
    }

    const serviceId = button.getAttribute("data-consent-accept")?.trim()?.toLowerCase() as ContextualConsentServiceId | null;
    if (!serviceId || !(serviceId in CONTEXTUAL_CONSENT_SERVICES)) return;

    evt.preventDefault();
    acceptContextualService(serviceId);
  });

  unlockEmbedComponentsIfAccepted();
}

export function initConsent(app: {
  preferences: {
    thirdParty: {
      necessary: boolean;
      functionality: boolean;
      analytics: boolean;
      marketing: boolean;
    };
    cmp: unknown;
  };
}) {
  app.preferences = {
    thirdParty: {
      necessary: true,
      functionality: false,
      analytics: false,
      marketing: false,
    },
    cmp: null as unknown,
  };

  if (!appConfig.enableCMP) {
    app.preferences.cmp = "Consent Management disabled";
    initContextualConsent();
    return;
  }

  const { gtmId } = appConfig;

  function loadServicesIfAccepted() {
    if (CookieConsent.acceptedService("Google Tag Manager", "analytics")) {
      loadGTM(gtmId);
    }
  }

  CookieConsent.run({
    guiOptions: {
      consentModal: {
        layout: "box",
        position: "bottom center",
        equalWeightButtons: true,
        flipButtons: false,
      },
      preferencesModal: {
        layout: "box",
        position: "right",
        equalWeightButtons: true,
        flipButtons: false,
      },
    },
    categories: {
      necessary: {
        readOnly: true,
      },
      functionality: {
        services: {
          "Pipedrive Webform": { label: "Pipedrive Webform" },
        },
      },
      analytics: {
        services: {
          "Google Tag Manager": {
            label: "Google Tag Manager",
            // Kein onAccept – GTM wird ausschließlich über onConsent → loadServicesIfAccepted geladen
          },
          "Google Analytics": { label: "Google Analytics" },
        },
      },
      marketing: {
        services: {
          YouTube: { label: "YouTube" },
          Vimeo: { label: "Vimeo" },
        },
      },
    },
    language: {
      default: "de",
      autoDetect: "document",
      translations: {
        de: {
          consentModal: {
            title: "Einwilligungsmanagement",
            description:
              "Unsere Website verwendet Tools & zugehörige Cookies von Drittanbietern, um die Sicherheit zu gewährleisten, Missbrauch zu verhindern, die Funktionalität zu erhöhen, Leistung zu verbessern oder um ihre Präferenzen zu speichern. Sie können die Verwendung optionaler Tools erlauben oder ablehnen.",
            acceptAllBtn: "Alles erlauben",
            acceptNecessaryBtn: "Alles ablehnen",
            showPreferencesBtn: "Einstellungen verwalten",
            footer: '<a href="/datenschutz">Datenschutz</a>',
          },
          preferencesModal: {
            title: "Präferenzen für die Zustimmung",
            acceptAllBtn: "Alles erlauben",
            acceptNecessaryBtn: "Alles ablehnen",
            savePreferencesBtn: "Einstellungen speichern",
            closeIconLabel: "Modal schließen",
            serviceCounterLabel: "Dienste",
            sections: [
              {
                title: "Verwendung von Tools & Cookies",
                description:
                  "Die folgenden Tools & Cookies werden verwendet, um die Sicherheit zu gewährleisten, Missbrauch zu verhindern, die Funktionalität zu erhöhen, Leistung zu verbessern oder um Ihre Präferenzen zu speichern.",
              },
              {
                title:
                  'Notwendig <span class="pm__badge">Immer Aktiviert</span>',
                description:
                  "Essentielle Cookies und Dienste werden verwendet, um grundlegende Funktionen der Website zu ermöglichen, wie beispielsweise die Gewährleistung der Sicherheit der Website.",
                linkedCategory: "necessary",
              },
              {
                title: "Funktionalität",
                description:
                  "Funktionale Cookies und Dienste werden verwendet, um erweiterte und personalisierte Funktionen anzubieten. Diese Technologien bieten zusätzliche Features und ein verbessertes Benutzererlebnis, indem sie beispielsweise Ihre Spracheinstellungen, Schriftgrößen, Regionenauswahlen und individuell angepasste Layouts speichern. Das Ablehnen dieser Cookies kann dazu führen, dass bestimmte Dienste oder Funktionen der Website nicht verfügbar sind.",
                linkedCategory: "functionality",
              },
              {
                title: "Analyse",
                description:
                  "Analyse-Cookies und -Dienste werden verwendet, um statistische Informationen darüber zu sammeln, wie Besucher mit einer Website interagieren. Diese Technologien liefern Einblicke in die Website-Nutzung, das Verhalten der Besucher und die Leistungsfähigkeit der Seite, um die Website zu verstehen, zu verbessern und das Benutzererlebnis zu optimieren.",
                linkedCategory: "analytics",
              },
              {
                title: "Marketing",
                description:
                  "Marketing-Cookies und -Dienste werden verwendet, um personalisierte Werbeanzeigen, Aktionen und Angebote bereitzustellen. Diese Technologien ermöglichen zielgerichtete Werbung und Marketingkampagnen, indem sie Informationen über die Interessen, Vorlieben und Online-Aktivitäten der Nutzer sammeln.",
                linkedCategory: "marketing",
              },
              {
                title: "Weitere Informationen",
                description:
                  'Für Rückfragen mit Hinblick auf den Einsatz von Cookies, Tools von Drittanbietern oder Ihren ausgewählten Präferenzen <a class="cc__link" href="/contact">kontaktieren Sie uns</a> gerne.',
              },
            ],
          },
        },
      },
    },
    disablePageInteraction: true,
    onConsent: () => {
      loadServicesIfAccepted();
      unlockEmbedComponentsIfAccepted();
    },
  });

  app.preferences.cmp = CookieConsent;

  initContextualConsent();

  window.addEventListener("cc:onModalShow", () => {
    Scroll.stop();
  });

  window.addEventListener("cc:onModalHide", () => {
    Scroll.start();
  });

  window.addEventListener("cc:onModalReady", (evt: Event) => {
    const { detail } = evt as CustomEvent;
    if (detail?.modalName === "preferencesModal" && detail?.modal) {
      const preferencesModalBody = detail.modal.querySelector(".pm__body");
      if (preferencesModalBody) {
        preferencesModalBody.setAttribute("data-lenis-prevent", "");
      }
    }
  });

  window.addEventListener("cc:onFirstConsent", () => {
    const acceptedCategories =
      CookieConsent.getUserPreferences().acceptedCategories;

    app.preferences.thirdParty = {
      necessary: true,
      functionality: acceptedCategories.includes("functionality"),
      analytics: acceptedCategories.includes("analytics"),
      marketing: acceptedCategories.includes("marketing"),
    };

    pushConsentUpdate(getConsentState());
    unlockEmbedComponentsIfAccepted();
  });

  window.addEventListener("cc:onChange", () => {
    const acceptedCategories =
      CookieConsent.getUserPreferences().acceptedCategories;

    app.preferences.thirdParty = {
      necessary: true,
      functionality: acceptedCategories.includes("functionality"),
      analytics: acceptedCategories.includes("analytics"),
      marketing: acceptedCategories.includes("marketing"),
    };

    pushConsentUpdate(getConsentState());
    unlockEmbedComponentsIfAccepted();
  });
}